import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Chip,
  TextField,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { transactionService } from '../services/transaction.service';
import { moldService } from '../services/mold.service';
import { Transaction, Mold, TransactionTypeMap, TransactionType } from '../types';
import { format } from 'date-fns';

const TransactionHistory: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [molds, setMolds] = useState<{ [key: string]: Mold }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'ALL' | 'check-out' | 'check-in'>('ALL');
  const [searchMoldNumber, setSearchMoldNumber] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // 입출고 내역 로드
      const transactionList = await transactionService.getTransactionHistory();
      setTransactions(transactionList);
      
      // 금형 정보 로드
      const moldList = await moldService.getMolds();
      const moldMap: { [key: string]: Mold } = {};
      moldList.forEach(mold => {
        if (mold.id) {
          moldMap[mold.id] = mold;
        }
      });
      setMolds(moldMap);
      
    } catch (error) {
      console.error('데이터 로드 실패:', error);
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };
  // 필터링된 거래 내역
  const filteredTransactions = useMemo(() => {
    return transactions.filter(transaction => {
      // 타입 필터
      if (filterType !== 'ALL' && transaction.type !== filterType) {
        return false;
      }

      // 금형 번호 검색
      if (searchMoldNumber) {
        const mold = molds[transaction.moldId];
        if (!mold || !mold.moldNumber.toLowerCase().includes(searchMoldNumber.toLowerCase())) {
          return false;
        }
      }

      // 날짜 필터
      if (startDate && transaction.timestamp.toDate() < startDate) {
        return false;
      }
      if (endDate && transaction.timestamp.toDate() > endDate) {
        return false;
      }

      return true;
    });
  }, [transactions, filterType, searchMoldNumber, startDate, endDate, molds]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    return format(timestamp.toDate(), 'yyyy-MM-dd HH:mm');
  };

  const formatShortDate = (date: any) => {
    if (!date) return '-';
    const dateObj = date.toDate ? date.toDate() : date;
    return format(dateObj, 'MM/dd');
  };

  const formatShortDateTime = (date: any) => {
    if (!date) return '-';
    const dateObj = date.toDate ? date.toDate() : date;
    return format(dateObj, 'MM/dd HH:mm');
  };

  const getTypeChip = (type: 'check-out' | 'check-in') => {
    const displayText = TransactionTypeMap[type];
    return (
      <Chip
        label={displayText === 'IN' ? '입고' : '출고'}
        color={displayText === 'IN' ? 'success' : 'primary'}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>로딩 중...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        입출고 내역
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 필터 섹션 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            검색 필터
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>타입</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as 'ALL' | 'check-out' | 'check-in')}
                >
                  <MenuItem value="ALL">전체</MenuItem>
                  <MenuItem value="check-out">출고</MenuItem>
                  <MenuItem value="check-in">입고</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <TextField
                label="금형 번호"
                value={searchMoldNumber}
                onChange={(e) => setSearchMoldNumber(e.target.value)}
                fullWidth
              />
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="시작일"
                  value={startDate}
                  onChange={setStartDate}
                  format="yyyy/MM/dd"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="종료일"
                  value={endDate}
                  onChange={setEndDate}
                  format="yyyy/MM/dd"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 내역 카드 섹션 */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        총 {filteredTransactions.length}건의 내역
      </Typography>

      {filteredTransactions.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              조건에 맞는 내역이 없습니다.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {filteredTransactions.map((transaction) => {
            const mold = molds[transaction.moldId];
            const isCheckOut = transaction.type === 'check-out';
            
            return (
              <Box 
                key={transaction.id}
                sx={{ 
                  flex: '0 0 auto',
                  width: { 
                    xs: '100%',
                    md: 'calc(50% - 12px)',
                    lg: 'calc(33.33% - 16px)'
                  }
                }}
              >
                <Card sx={{ 
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}>
                  <CardContent>
                    {/* 헤더 */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      mb: 2 
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        {getTypeChip(transaction.type as TransactionType)}
                        <Typography variant="body1" sx={{ 
                          fontWeight: 'bold',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {mold ? mold.moldNumber : transaction.moldId}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
                        {formatShortDateTime(transaction.timestamp)}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* 금형 정보 */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {mold ? mold.moldName : '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mold ? mold.customerName : '-'}
                      </Typography>
                    </Box>

                    {/* 출고 정보 */}
                    {isCheckOut && (
                      <Box sx={{ mb: 1 }}>
                        {transaction.purpose && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="body2">• 작업:</Typography>
                            <Typography variant="body2">{transaction.purpose}</Typography>
                          </Box>
                        )}
                        {transaction.shots != null && transaction.shots > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="body2">• 쇼트:</Typography>
                            <Typography variant="body2">{transaction.shots.toLocaleString()}</Typography>
                          </Box>
                        )}
                        {transaction.expectedReturn && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="body2">• 반납예정:</Typography>
                            <Typography variant="body2">{formatShortDate(transaction.expectedReturn)}</Typography>
                          </Box>
                        )}
                        {!transaction.actualReturn && (
                          <Box sx={{ mt: 1 }}>
                            <Chip label="사용 중" color="warning" size="small" variant="outlined" />
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* 입고 정보 */}
                    {!isCheckOut && (
                      <Box sx={{ mb: 1 }}>
                        {transaction.purpose && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="body2">• 작업완료:</Typography>
                            <Typography variant="body2">{transaction.purpose}</Typography>
                          </Box>
                        )}
                        {transaction.shots != null && transaction.shots > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="body2">• 실제쇼트:</Typography>
                            <Typography variant="body2">{transaction.shots.toLocaleString()}</Typography>
                          </Box>
                        )}
                        {transaction.actualReturn && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="body2">• 반납:</Typography>
                            <Typography variant="body2" color="success.main">
                              {formatShortDateTime(transaction.actualReturn)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* 세부 설명 (작은 글씨) */}
                    {(transaction.notes || (transaction as any).description) && (
                      <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          display: 'block',
                          wordBreak: 'break-word'
                        }}>
                          {transaction.notes || (transaction as any).description}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default TransactionHistory;

